<template>
    <div>
        <div v-if="!loading">
            <plan-history
                :params="messageParams" :key="`student-plan-`+currentStudent && currentStudent.student_id"
                @loaded-student="$emit('loaded-student')"
                :reload="reload"
                @setInterventionId="$emit('setInterventionId')"
                :currentPlan="currentPlan"
                isAttendance
            />

            <div class="d-flex flex-column flex-fill student-attendance-section">
                <div class="d-flex flex-column flex-fill student-attendance-subsection">
                    <div style="font-size: 18px; padding: 0 16px;">
                        Reports
                    </div>
                    <div class="d-flex">
                        <div class="pdf-container flex-grow-1" 
                            role="button"
                            tabindex="0"
                            @keydown.enter="creationReport"
                            @keydown.space.prevent="creationReport"
                            aria-label="Creation Report"
                            data-test="studentview-creation-report-btn"
                            @click="creationReport"
                        >
                            <v-icon class="mx-4">far fa-file-pdf</v-icon>
                            Creation Report
                        </div>
                        <div class="pdf-container flex-grow-1" 
                            role="button"
                            tabindex="0"
                            @keydown.enter="fidelityReport"
                            @keydown.space.prevent="fidelityReport"
                            aria-label="Fidelity Report"
                            data-test="studentview-fidelity-report-btn"
                            @click="fidelityReport"
                        >
                            <v-icon class="mx-4">far fa-file-pdf</v-icon>
                            Fidelity Report
                        </div>
                    </div>
                    <div v-if="!hideReportGrid" class="d-flex flex-fill flex-column">
                        <list
                            style="margin: 0 16px; height: auto;"
                            :params="formsParams"
                            :showToolbar="false"
                            intervention
                            @loaded="loadedForms"
                            domLayout="autoHeight"
                            class="student-attendance-forms-list"
                        />
                    </div>
                </div>
            </div>

            <div v-if="attachments.length > 0"  class="d-flex flex-column flex-fill student-attendance-section">
                <div class="d-flex flex-column flex-fill student-attendance-subsection">
                    <div style="font-size: 18px; padding: 0 16px;">
                        Attachments
                    </div>
                    <div v-for="attachment in attachments" :key="attachment.id" style="font-size: 20px;" class="ma-3">
                        <v-icon class="studentview-attachment">far fa-paperclip</v-icon>
                        <span class="mx-3 studentview-attachment studentview-attachment-title" @click="viewAttachment(attachment)" data-test="studentview-attachment-btn" aria-label="View Attachment">{{ attachment.name }}</span>
                        <v-icon class="studentview-attachment" @click="downloadAttachment(attachment)" data-test="studentview-download-attachment-btn" aria-label="Download Attachment">far fa-arrow-alt-to-bottom</v-icon>
                    </div>
                </div>
            </div>

            <div class="d-flex flex-column flex-fill student-attendance-section">
                <div  class="d-flex flex-column flex-fill student-attendance-subsection message-container">
                    <div style="font-size: 18px; padding: 0 16px;">Comments</div>
                    <message
                        :key="`messagetab${currentStudent && currentStudent.student_id}`"
                        style="margin: 16px;"
                        messageModel="interventionPlanComments"
                        :params="messageParams"
                        removeMargin
                    />
                </div>
            </div>

            <fe-dialog
                v-if="printing"
                width="80%"
                title="Print Summary"
                @close="printing = false"
                @cancel="printing = false"
            >
                <iframe style="height: 60vh;" class="pdf-iframe" :src="pdfUrl"/>
            </fe-dialog>

            <fe-crud
                :autoload="false"
                ref="attachmentCrud"
                :config="$models.interventionTypeAttachment"
                @read="v => attachments = v"
            />
            <fe-crud
                autoload
                ref="typeCrud"
                :config="$models.interventionType"
                @read="v => interventionTypes = v"
            />

            <pdf-view
                v-if="showPdf"
                @close="showPdf=false"
                :pdfData="pdfData"
                :viewType="viewType"
                headerColor="white"
                headerTextColor="#495b8f"
            />
        </div>
        <div v-else>
            <fe-spinner class="spinner"/>
        </div>
    </div>
</template>

<script>
import PlanHistory from './PlanHistory'
import List from '@/components/modules/smartforms/List'
import Message from '@/components/common/Message'
import PdfView from '@/components/modules/studentprofile/attachments/PdfView'

export default {
    name: 'StudentAttendanceView',
    components: {
        PlanHistory,
        List,
        Message,
        PdfView
    },
    props: {
        messageParams: {
            type: Object,
            default: () => {}
        },
        currentStudent: {
            type: Object,
            default: () => {}
        },
        currentPlan: {
            type: Object,
            default: () => {}
        },
        reload: {
            type: Boolean,
            default: false
        },
        formsParams: {
            type: Object,
            default: () => {}
        },
    },
    computed: {
        interventionTypeId() {
                return this.interventionTypes.find(type => type.name === this.currentPlan?.intervention_type_name)?.id
            }
    },
    watch: {
        currentStudent: {
            handler(v, oldVal) {
                if (v?.intervention_plan_id) {
                    this.loading = false
                } else {
                    this.loading = true
                }
                if (v?.student_id !== oldVal?.student_id) {
                    // need to reload the forms list
                    this.hideReportGrid = false
                }
            },
            immediate: true,
        },
        interventionTypeId(v) {
            if(v) {
                this.$refs.attachmentCrud.read({intervention_type_id: v})
            }
        },
    },
    data() {
        return {
            printing: false,
            pdfUrl: '',
            hideReportGrid: false,
            loading: true,
            interventionTypes: [],
            attachments: [],
            showPdf: false,
            pdfData: null,
            viewType: null,
        }
    },
    methods: {
        creationReport() {
            let host = this.$axios.defaults.baseURL
            this.pdfUrl = `${host}/reportCreate.php?type=intervention_creation&student_id=${this.currentStudent?.student_id}&intervention_id=${this.currentStudent?.intervention_id}`
            this.printing = true
        },
        fidelityReport() {
            let host = this.$axios.defaults.baseURL
            this.pdfUrl = `${host}/reportCreate.php?type=intervention_fidelity&student_id=${this.currentStudent?.student_id}&intervention_id=${this.currentStudent?.intervention_id}`
            this.printing = true
        },
        loadedForms(forms) {
            this.hideReportGrid = forms.length === 0
        },
        downloadAttachment(attachment) {
            this.$downloadFile('interventionTypes.php?action=get&property=file&intervention_type_id='+attachment.intervention_type_id+'&id='+attachment.id,attachment.name)
        },
        viewAttachment(attachment) {
            let attachments = '&property=file&intervention_type_id='+attachment.intervention_type_id+'&id='+attachment.id

            if (attachment.file_type.indexOf('image/') > -1) {
                this.$axios.get(
                    this.$models.getUrl('interventionType', 'read') + attachments, {
                        responseType: "blob",
                        headers: {
                            'Content-Type': 'image/*'
                        }
                    }
                )
                .then(response => {
                    let reader = new FileReader()
                    reader.readAsDataURL(response.data)
                    reader.onload = () => {
                        this.pdfData = reader.result
                        this.viewType = 'image'
                    }
                })
                .finally(() => {
                    this.showPdf = true
                })
            } else {
                this.$axios.get(
                    this.$models.getUrl('interventionType', 'read') + attachments, {
                        responseType: "arraybuffer",
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                )
                .then(response => {
                    const blob = new Blob([response.data], { type: 'application/pdf' })
                    const url = window.URL.createObjectURL(blob)
                    this.pdfData = url
                    this.viewType = 'pdf'
                })
                .finally(() => {
                    this.showPdf = true
                })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.student-attendance {
    &-subsection {
        background: white;
        border-radius: 4px;
        border: 1px solid #E0E1E6;
        padding: 16px 12px;
    }
    &-section {
        background: #F5F6F8;
        padding: 0 16px;
        overflow: scroll;
    }
    &-forms-list {
        ::v-deep .ag-center-cols-clipper {
            min-height: 40px !important;
        }
    }
}
.pdf-container {
    line-height: 28px;
    border: 1px solid #dddddd;
    margin: 16px;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background: var(--fe-accent);
    }
}
.message-container {
    padding-bottom: 0;
}
.spinner {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    line-height: 250px !important;
}
.studentview-attachment {
    color:#146EB3; 
    font-size: 20px;
    &-title {
        cursor: pointer; 
        border-bottom: 1px solid #146EB3;
        line-height: 19px;
        display: inline-block;
    }
}
</style>
